<template>
    <nav v-if="showBreadcrumb"
         ref="navElement"
         class="o-chrome-container min-h-[3.5rem]"
         aria-label="Breadcrumb">
        <transition-group tag="ul"
                          name="list-fade"
                          appear
                          class="w-full overflow-y-hidden overflow-x-hidden whitespace-nowrap relative"
                          @before-leave="pinLeavePosition">
            <li v-for="(crumb, index) in breadcrumbs"
                :key="crumb.url"
                class="-no-wrap inline-block">
                <template v-if="index < breadcrumbs.length - 1">
                    <CultureAwareRouterLink :to="crumb.page ? { name: crumb.page } : crumb.url"
                                            class="font-medium text-11 text-gray-900 underline hover:text-gray-800 leading-large py-10">
                        <span>{{ crumb.name }}</span>
                    </CultureAwareRouterLink>
                    <CIcon
                        class="mx-6"
                        name="arrow-right"
                        width="6"
                        height="6"/>
                </template>
                <span v-else
                      class="text-11 text-gray-900 leading-large py-10">
                    <span aria-current="page">{{ crumb.name }}</span>
                </span>
            </li>
        </transition-group>
    </nav>
</template>

<script setup lang="ts">
import { computed, onBeforeUpdate, useTemplateRef } from 'vue';
import appStore from '@/core/app/app.store';
import { BreadcrumbItem } from '@/types/contentServerContract';

const breadcrumbs = computed((): BreadcrumbItem[] => {
    return [...appStore.breadcrumbPrettified];
});

const showBreadcrumb = computed((): boolean => {
    return !appStore.hideBreadcrumb;
});

const navElement = useTemplateRef<HTMLElement>('navElement');
const leavePositions = new WeakMap<HTMLElement, { left: number; top: number }>();

onBeforeUpdate(() => {
    const list = navElement.value?.firstElementChild;
    if (!list) return;
    for (const item of Array.from(list.children) as HTMLElement[]) {
        leavePositions.set(item, { left: item.offsetLeft, top: item.offsetTop });
    }
});

function pinLeavePosition(el: Element) {
    const item = el as HTMLElement;
    const pinned = leavePositions.get(item);
    item.style.left = `${pinned ? pinned.left : item.offsetLeft}px`;
    item.style.top = `${pinned ? pinned.top : item.offsetTop}px`;
}
</script>

<style lang="less" scoped>
.list-fade-enter-active,
.list-fade-leave-active {
    transition: opacity 0.2s ease;
}

.list-fade-enter-from,
.list-fade-leave-to {
    opacity: 0;
}

.list-fade-leave-active {
    position: absolute;
}

.-no-wrap {
    white-space: nowrap;
}
</style>
